import { z } from 'zod';
import { nutrientsSchema } from '../nutrients.js';
import { booleanParam, quantityUnitSchema } from '../units.js';
export const ingredientInputSchema = z
    .object({
    foodId: z.string().uuid().nullish(),
    subRecipeId: z.string().uuid().nullish(),
    quantity: z.number().nonnegative().nullish(),
    unit: quantityUnitSchema.nullish(),
    measureLabel: z.string().max(40).nullish(),
    rawText: z.string().min(1).max(300),
    note: z.string().max(300).nullish(),
    optional: z.boolean().default(false),
    confidence: z.number().min(0).max(1).nullish(),
})
    .refine((v) => !(v.foodId && v.subRecipeId), {
    message: 'An ingredient line is either a food or a sub-recipe, not both',
})
    .refine((v) => v.unit !== 'measure' || Boolean(v.measureLabel), {
    message: 'A measure quantity needs a measure label',
});
export const stepInputSchema = z.object({
    text: z.string().min(1).max(2000),
    timerSeconds: z.number().int().positive().nullish(),
});
/** When a recipe is eaten. Several at once is normal; kept apart from the plan's meal_slot. */
export const mealTypeSchema = z.enum([
    'breakfast',
    'brunch',
    'lunch',
    'dinner',
    'starter',
    'side',
    'dessert',
    'snack',
    'drink',
]);
export const MEAL_TYPES = mealTypeSchema.options;
/** A place in the household's category tree, as the API returns it. */
export const recipeCategorySchema = z.object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    name: z.string(),
    position: z.number().int(),
    /** Recipes linked to this category itself, not its descendants. */
    recipeCount: z.number().int(),
});
export const recipeCategoryInputSchema = z.object({
    name: z.string().min(1).max(60),
    parentId: z.string().uuid().nullish(),
});
export const recipeCategoryPatchSchema = z
    .object({
    name: z.string().min(1).max(60).optional(),
    /** Null moves it to the top level. */
    parentId: z.string().uuid().nullable().optional(),
    position: z.number().int().min(0).optional(),
})
    .refine((v) => v.name !== undefined || v.parentId !== undefined || v.position !== undefined, {
    message: 'Nothing to change',
});
/** A category on a recipe, with its path from the top: "Italian › Pasta". */
export const recipeCategoryRefSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    path: z.array(z.string()),
});
export const recipeInputSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).nullish(),
    servings: z.number().int().positive().max(100),
    cookedWeightG: z.number().positive().nullish(),
    yieldFactor: z.number().positive().max(2).nullish(),
    prepMin: z.number().int().nonnegative().max(2880).nullish(),
    cookMin: z.number().int().nonnegative().max(2880).nullish(),
    tags: z.array(z.string().max(40)).max(20).default([]),
    mealTypes: z.array(mealTypeSchema).max(9).default([]),
    categoryIds: z.array(z.string().uuid()).max(20).default([]),
    sourceUrl: z.string().url().nullish(),
    ingredients: z.array(ingredientInputSchema).max(100).default([]),
    steps: z.array(stepInputSchema).max(100).default([]),
    status: z.enum(['draft', 'ready']).default('ready'),
});
export const nutritionSummarySchema = z.object({
    perServing: nutrientsSchema,
    per100g: nutrientsSchema,
    rawWeightG: z.number(),
    cookedWeightG: z.number(),
    /**
     * A counted ingredient (ADR-028) and no cooked weight: the recipe knows its
     * servings but not its grams, so per 100 g is not given, and this says why.
     */
    weightIncomplete: z.boolean().default(false),
    /** Nutrients no ingredient could supply, shown as "incomplete data". */
    unknownNutrients: z.array(z.string()),
    computedAt: z.date(),
});
export const recipeIngredientSchema = z.object({
    id: z.string().uuid(),
    position: z.number().int(),
    foodId: z.string().uuid().nullable(),
    foodName: z.string().nullable(),
    subRecipeId: z.string().uuid().nullable(),
    subRecipeTitle: z.string().nullable(),
    quantity: z.number().nullable(),
    unit: quantityUnitSchema.nullable(),
    measureLabel: z.string().nullable(),
    grams: z.number().nullable(),
    /**
     * A counted line of a food published per item (ADR-028). It has no grams
     * on purpose and still counts; it is not an ingredient we failed to weigh.
     */
    counted: z.boolean().default(false),
    rawText: z.string(),
    note: z.string().nullable(),
    optional: z.boolean(),
    confidence: z.number().nullable(),
});
export const recipeSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    servings: z.number().int(),
    cookedWeightG: z.number().nullable(),
    yieldFactor: z.number().nullable(),
    prepMin: z.number().int().nullable(),
    cookMin: z.number().int().nullable(),
    tags: z.array(z.string()),
    mealTypes: z.array(mealTypeSchema),
    categories: z.array(recipeCategoryRefSchema),
    sourceUrl: z.string().nullable(),
    /** Where this recipe's photograph lives, or null. A key, never a URL. */
    imageKey: z.string().nullable(),
    isStarter: z.boolean(),
    status: z.enum(['draft', 'ready']),
    ingredients: z.array(recipeIngredientSchema),
    steps: z.array(z.object({
        id: z.string().uuid(),
        position: z.number().int(),
        text: z.string(),
        timerSeconds: z.number().int().nullable(),
    })),
    nutrition: nutritionSummarySchema.nullable(),
    /** Licence attributions for every source the nutrition draws on (M-12). */
    attributions: z.array(z.string()),
});
export const recipeSummarySchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid().nullable(),
    title: z.string(),
    servings: z.number().int(),
    prepMin: z.number().int().nullable(),
    cookMin: z.number().int().nullable(),
    tags: z.array(z.string()),
    mealTypes: z.array(mealTypeSchema),
    categories: z.array(recipeCategoryRefSchema),
    isStarter: z.boolean(),
    status: z.enum(['draft', 'ready']),
    energyKcalPerServing: z.number().nullable(),
    proteinGPerServing: z.number().nullable(),
    /** The signed-in person's own stars, 1 to 5, or null (S-24). */
    myStars: z.number().int().nullable().default(null),
    /** One of the signed-in person's favourites; these come first in a list. */
    favourite: z.boolean().default(false),
});
export const recipeListQuerySchema = z.object({
    q: z.string().max(120).optional(),
    tag: z.string().max(40).optional(),
    mealType: mealTypeSchema.optional(),
    /** This category or any of its descendants. */
    categoryId: z.string().uuid().optional(),
    includeStarters: booleanParam(true),
    /** Only the signed-in person's favourites. */
    favourites: booleanParam(false),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /** Where the page starts; pages are stable because the order ends on id. */
    offset: z.coerce.number().int().min(0).default(0),
});
export const scaleQuerySchema = z.object({
    servings: z.coerce.number().positive().max(200),
});
export const parseIngredientsRequestSchema = z.object({
    lines: z.array(z.string().min(1).max(300)).min(1).max(60),
});
export const parsedLineSchema = z.object({
    raw: z.string(),
    quantity: z.number().nullable(),
    unit: quantityUnitSchema.nullable(),
    measureLabel: z.string().nullable(),
    note: z.string().nullable(),
    optional: z.boolean(),
    confidence: z.number(),
    /** Ranked matches; the client confirms when confidence is low. */
    candidates: z.array(z.object({
        foodId: z.string().uuid(),
        name: z.string(),
        brand: z.string().nullable(),
        grams: z.number().nullable(),
    })),
    /** True when the client must ask before the line can be saved. */
    needsConfirmation: z.boolean(),
});
export const parseIngredientsResponseSchema = z.object({
    lines: z.array(parsedLineSchema),
});
export const importRecipeRequestSchema = z.object({
    url: z.string().url(),
});
/** One person's view of a recipe (S-24). */
export const recipeRatingSchema = z.object({
    stars: z.number().int().min(1).max(5).nullable(),
    favourite: z.boolean(),
});
/** Either or both; `stars: null` clears the rating. */
export const recipeRatingInputSchema = z
    .object({
    stars: z.number().int().min(1).max(5).nullable().optional(),
    favourite: z.boolean().optional(),
})
    .refine((v) => v.stars !== undefined || v.favourite !== undefined, { message: 'Give stars, favourite, or both' });
//# sourceMappingURL=recipe.js.map